/**
 * Click-to-open comments popover (#2251 follow-up for caption normal
 * view).
 *
 * The right-hand gutter design (`comments-gutter.tsx`) works for the
 * transcript editor where the right rail is reserved for comments,
 * but in the caption editor's normal view the right side belongs to
 * the media player — the always-visible gutter was overlaying the
 * video. This component shows ONE card at a time, anchored next to
 * the leaf marker the user clicked, matching the pre-gutter popover
 * behaviour.
 *
 * Discovery still comes from the amber / emerald leaf adornments
 * (`use-leaf-comment-adornments.tsx`); the adornment icon dispatches
 * `transcript-comments:focus` with the comment id + icon rect and
 * this component picks it up. The draft (compose) card is handled
 * the same way — the toolbar fires `startDraft`, we anchor the
 * compose card at the selection's end-word rect.
 */

/* eslint-disable no-restricted-syntax -- cards stay white regardless
   of theme (same rule as `gutter-slot.tsx`). */

import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Comment } from '@utils/types/comments/comments.ts';
import { CommentCard } from './comment-card.tsx';
import { useCommentsContext } from './comments-context.tsx';
import { useCommentFilter } from './comments-filter-context.tsx';
import { filterFlagsFor } from './comments-selectors.ts';

interface IconRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface Props {
  /** Scroll container used to resolve word rects for the compose
   *  draft (so the draft lands next to the user's selection). */
  scrollRootRef: React.RefObject<HTMLElement | null>;
  /** Max width of the card body. Clamps to the viewport if narrower. */
  maxCardWidth?: number;
}

const CARD_MARGIN = 8;

function clampToViewport(
  desiredLeft: number,
  desiredTop: number,
  cardWidth: number,
  cardHeight: number
): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(
    Math.max(CARD_MARGIN, desiredLeft),
    vw - cardWidth - CARD_MARGIN
  );
  const top = Math.min(
    Math.max(CARD_MARGIN, desiredTop),
    vh - cardHeight - CARD_MARGIN
  );
  return { left, top };
}

function wordRect(
  indexFrom: string | undefined,
  indexTo: string | undefined,
  scrollRoot: HTMLElement | null
): IconRect | null {
  if (!scrollRoot) return null;
  const findWord = (attr: 'start' | 'stop', value: string | undefined) =>
    value
      ? scrollRoot.querySelector<HTMLElement>(
          `[data-${attr}="${CSS.escape(value)}"]`
        )
      : null;
  const word = findWord('stop', indexTo) ?? findWord('start', indexFrom);
  if (!word) return null;
  const r = word.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
}

export function CommentsPopover({ scrollRootRef, maxCardWidth = 320 }: Props) {
  const ctx = useCommentsContext();
  const { t } = useTranslation();
  const { filter } = useCommentFilter();
  const { showOpen, showResolved } = filterFlagsFor(filter);

  const [active, setActive] = useState<{
    key: string;
    iconRect: IconRect;
  } | null>(null);
  const [cardHeight, setCardHeight] = useState(0);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Marker click → open the popover at the icon rect.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (
        e as CustomEvent<{
          commentId: number | string;
          iconRect?: IconRect;
        }>
      ).detail;
      if (!detail?.commentId || !detail.iconRect) return;
      setActive({
        key: `c:${detail.commentId}`,
        iconRect: detail.iconRect
      });
    };
    window.addEventListener('transcript-comments:focus', handler);
    return () =>
      window.removeEventListener('transcript-comments:focus', handler);
  }, []);

  // Outside click + Escape close the popover (and discard any draft).
  useEffect(() => {
    if (!ctx) return;
    const hasPopover = active != null || ctx.draft != null;
    if (!hasPopover) return;
    const onMouseDown = (ev: MouseEvent) => {
      if (panelRef.current?.contains(ev.target as Node)) return;
      setActive(null);
      if (ctx.draft) ctx.cancelDraft();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setActive(null);
        if (ctx.draft) ctx.cancelDraft();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [active, ctx]);

  const measureCard = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    if (!el) return;
    const read = () =>
      setCardHeight(Math.round(el.getBoundingClientRect().height));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!ctx) return null;

  // Pick which card to render. Draft wins — compose is a user action
  // in progress and shouldn't be preempted by a stray marker click.
  const draft = ctx.draft;
  const comment: Comment | undefined = active
    ? ctx.allComments.find(c => `c:${c.id}` === active.key)
    : undefined;

  // Respect the Open / Resolved / All filter — don't show a resolved
  // card when the filter is Open (#2246 reopen behaviour).
  const matchesFilter = comment
    ? (comment.resolved && showResolved) || (!comment.resolved && showOpen)
    : true;

  if (!draft && (!comment || !matchesFilter)) return null;

  const anchor: IconRect | null = draft
    ? wordRect(draft.indexFrom, draft.indexTo, scrollRootRef.current)
    : (active?.iconRect ?? null);
  if (!anchor) return null;

  // Open rightward from the icon's left edge when it sits in the
  // viewport's left half, otherwise leftward from its right edge.
  const desiredWidth = Math.min(maxCardWidth, window.innerWidth - 16);
  const iconCenterX = (anchor.left + anchor.right) / 2;
  const openOnRight = iconCenterX < window.innerWidth / 2;
  const desiredLeft = openOnRight ? anchor.left : anchor.right - desiredWidth;
  const desiredTop = anchor.bottom + 6;
  const { left, top } = clampToViewport(
    desiredLeft,
    desiredTop,
    desiredWidth,
    cardHeight || 120
  );

  const panelStyle: CSSProperties = {
    position: 'fixed',
    top,
    left,
    width: desiredWidth,
    zIndex: 60,
    pointerEvents: 'auto'
  };

  return (
    <div
      role="dialog"
      aria-label={t('comments')}
      ref={measureCard}
      style={panelStyle}
      className="rounded-xl overflow-hidden"
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          color: '#111827',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow:
            '0 10px 24px -8px rgba(0,0,0,0.18), 0 2px 6px -2px rgba(0,0,0,0.08)'
        }}
        className="rounded-xl overflow-hidden"
      >
        {draft ? (
          <CommentCard
            id="draft"
            authorName={t('you')}
            createdAt={undefined}
            initialText=""
            initialMode="edit"
            onSave={async next => {
              await ctx.createOne({
                text: next,
                indexFrom: draft.indexFrom,
                indexTo: draft.indexTo
              });
              ctx.cancelDraft();
            }}
            onCancel={ctx.cancelDraft}
          />
        ) : (
          comment && (
            <CommentCard
              id={`c:${comment.id}`}
              authorName={comment.created_by_name}
              createdAt={comment.created}
              initialText={comment.text ?? ''}
              resolved={!!comment.resolved}
              readOnly={ctx.readOnly}
              ownershipLabel={ctx.ownershipLabel}
              onSave={async next => {
                await ctx.updateOne(
                  comment.id,
                  next,
                  comment.index_from ?? '',
                  comment.index_to ?? ''
                );
              }}
              onDelete={
                ctx.readOnly
                  ? undefined
                  : async () => {
                      await ctx.deleteOne(comment.id);
                      setActive(null);
                    }
              }
              onToggleResolved={
                ctx.readOnly ? undefined : () => ctx.toggleResolved(comment)
              }
            />
          )
        )}
      </div>
    </div>
  );
}
